import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import { Skeleton } from "@/components/ui/skeleton";

// Import fallback images
import hyundaiLogo from "@/assets/clients/hyundai.png";
import rogersLogo from "@/assets/clients/rogers.png";
import bravoLogo from "@/assets/clients/bravo.png";
import craLogo from "@/assets/clients/cra.jpg";
import electionsCanadaLogo from "@/assets/clients/elections-canada.jpg";
import ottawaFoodBankLogo from "@/assets/clients/ottawa-food-bank.png";
import canadianBloodServicesLogo from "@/assets/clients/canadian-blood-services.jpg";
import phacLogo from "@/assets/clients/phac.png";
import firstAirLogo from "@/assets/clients/first-air.png";
import justePourRireLogo from "@/assets/clients/juste-pour-rire.png";
import hondaLogo from "@/assets/clients/honda.png";
import pipscLogo from "@/assets/clients/pipsc.png";
import omaLogo from "@/assets/clients/oma.png";
import casmLogo from "@/assets/clients/casm.png";
import primeMinisterLogo from "@/assets/clients/prime-minister.jpg";
import hpascLogo from "@/assets/clients/hpasc.png";
import balestraLogo from "@/assets/clients/balestra.jpg";

const fallbackLogos: Record<string, string> = {
  "Hyundai": hyundaiLogo,
  "Honda": hondaLogo,
  "Rogers": rogersLogo,
  "CRA": craLogo,
  "Elections Canada": electionsCanadaLogo,
  "Canadian Blood Services": canadianBloodServicesLogo,
  "Public Health Agency of Canada": phacLogo,
  "First Air": firstAirLogo,
  "Juste Pour Rire": justePourRireLogo,
  "Ottawa Food Bank": ottawaFoodBankLogo,
  "Bravo": bravoLogo,
  "PIPSC": pipscLogo,
  "Ontario Medical Association": omaLogo,
  "Canada Aviation and Space Museum": casmLogo,
  "Prime Minister's Office": primeMinisterLogo,
  "Health Products and Food Branch": hpascLogo,
  "Balestra Productions": balestraLogo,
};

const ClientLogos = () => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = (clientName: string) => {
    setFailedImages(prev => new Set(prev).add(clientName));
  };

  const { data: clients, isLoading } = useQuery({
    queryKey: ["public-clients"],
    queryFn: async () => {
      // Use the clients_public view which excludes sensitive contact information
      const { data, error } = await supabase
        .from("clients_public" as any)
        .select("id, name, logo_url, website_url, testimonial, display_order, is_active")
        .order("display_order");
      if (error) throw error;
      return data as unknown as { id: string; name: string; logo_url: string | null; website_url: string | null; testimonial: string | null; display_order: number; is_active: boolean }[];
    },
  });

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const scrollAmount = 200;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const getLogoUrl = (client: { name: string; logo_url: string | null }) => {
    // If the image failed to load from database URL, use fallback
    if (failedImages.has(client.name)) {
      return fallbackLogos[client.name] || null;
    }
    // Prioritize database URL if it exists
    if (client.logo_url) {
      return client.logo_url;
    }
    // Fall back to local assets if no database URL
    return fallbackLogos[client.name] || null;
  };

  if (isLoading) {
    return (
      <section className="py-16 bg-white overflow-hidden">
        <div className="container-wide mb-10">
          <div className="text-center">
            <Skeleton className="h-4 w-48 mx-auto" />
          </div>
        </div>
        <div className="flex justify-center gap-12">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="w-36 h-14" />
          ))}
        </div>
      </section>
    );
  }

  if (!clients || clients.length === 0) {
    return null;
  }

  return (
    <section className="py-12 sm:py-16 bg-white overflow-hidden">
      <div className="container-wide mb-8 sm:mb-10">
        <ScrollReveal className="text-center">
          <p className="text-xs sm:text-sm uppercase tracking-widest text-muted-foreground font-medium">
            {t("clients.title")}
          </p>
        </ScrollReveal>
      </div>

      {/* Carousel container */}
      <div 
        className="relative group"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Left arrow */}
        <button
          onClick={() => scroll("left")}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/80 backdrop-blur-sm border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-all md:opacity-0 md:group-hover:opacity-100"
          aria-label="Scroll left"
        >
          <ChevronLeft size={20} />
        </button>

        {/* Right arrow */}
        <button
          onClick={() => scroll("right")}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/80 backdrop-blur-sm border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-all md:opacity-0 md:group-hover:opacity-100"
          aria-label="Scroll right"
        >
          <ChevronRight size={20} />
        </button>

        {/* Scrollable container with auto-scroll */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <div className={`flex ${isPaused ? "" : "animate-marquee"}`}>
            {/* First set of logos */}
            {clients.map((client) => {
              const logoUrl = getLogoUrl(client);
              return (
                <div
                  key={client.id}
                  className="flex-shrink-0 mx-8 md:mx-12"
                >
                  <div className="w-28 h-16 md:w-36 md:h-20 flex items-center justify-center grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt={client.name}
                        className="max-w-full max-h-full object-contain"
                        onError={() => handleImageError(client.name)}
                      />
                    ) : (
                      <span className="font-display text-sm md:text-base font-semibold tracking-tight whitespace-nowrap text-muted-foreground">
                        {client.name}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {/* Duplicate for seamless loop */}
            {clients.map((client) => {
              const logoUrl = getLogoUrl(client);
              return (
                <div
                  key={`${client.id}-duplicate`}
                  className="flex-shrink-0 mx-8 md:mx-12"
                >
                  <div className="w-28 h-16 md:w-36 md:h-20 flex items-center justify-center grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt={client.name}
                        className="max-w-full max-h-full object-contain"
                        onError={() => handleImageError(client.name)}
                      />
                    ) : (
                      <span className="font-display text-sm md:text-base font-semibold tracking-tight whitespace-nowrap text-muted-foreground">
                        {client.name}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientLogos;
